import React, { ReactElement, Fragment, useState, useEffect } from "react";
import { useAppSelector } from "redux/hooks";
import Box from "@mui/material/Box";
import { Typography } from "@mui/material";
import { Translate } from "react-redux-i18n";
import { Document } from "@react-pdf/renderer";

import { IEventData, IMarketConfig, IOddConfig } from "redux/sport/sportSlice";
import { groupdDataByTournamentId, groupdDataByDate, groupdDataByDateAndTournament, recursiveSearchByType, splitContentIntoPages, splitContentIntoPagesDateTournament, convertBeginToDate, convertOddValue } from "services/utils";
import styles from "./styles.module.scss";
interface IProps {
  order: string;
  selectedMarkets: number[],
  orientation: string;
  itemsToLoad: number;
}

function PrintOddsPdf({ order, selectedMarkets, orientation, itemsToLoad }: IProps): ReactElement {
  const oddsToPrint = useAppSelector(state => state.sport.oddsToPrint);
  const marketsConfig = useAppSelector(state => state.sport.marketsConfig);
  const sports = useAppSelector(state => state.sport.sports);
  const pageHeight = orientation === "landscape" ? 794 : 1122;
  const [pages, setPages] = useState<any>([])
  const [dataGrouped, setDataGrouped] = useState<any>()

  useEffect(() => {
    order === "tournament" ?
      setDataGrouped(groupdDataByTournamentId(oddsToPrint, sports, marketsConfig))
      : order === "date" ?
        setDataGrouped(
          groupdDataByDate(oddsToPrint, sports, marketsConfig)
        )
        : setDataGrouped(groupdDataByDateAndTournament(oddsToPrint, sports, marketsConfig))
  }, [oddsToPrint, marketsConfig, sports])

  useEffect(() => {
    if (dataGrouped && order !== "date-tournament") {
      setPages(splitContentIntoPages(dataGrouped, orientation, pageHeight, sports, order))
    } else {
      setPages(splitContentIntoPagesDateTournament(dataGrouped, orientation, pageHeight, sports))
    }
  }, [dataGrouped, orientation])

  return (
    <Box id="printDoc">
      <Document>
        {pages && pages.length > 0 && pages.slice(0, itemsToLoad).map((page: any) =>
          <Box key={page[0].key} >
            {
              order === "tournament" &&
              <OrderByTournamentTemplate orientation={orientation} selectedMarkets={selectedMarkets} dataGrouped={page} />
            }
            {
              order === "date" &&
              <OrderByDateTemplate orientation={orientation} selectedMarkets={selectedMarkets} dataGrouped={page} />
            }
            {
              order === "date-tournament" &&
              <OrderByDateTournamentTemplate orientation={orientation} selectedMarkets={selectedMarkets} dataGrouped={page} />
            }
          </Box>
        )}
      </Document>
    </Box>
  )
}
export default PrintOddsPdf;

type OrderByTournamentTemplateProps = {
  selectedMarkets: number[]
  orientation: string
  caller?: string
  dataGrouped: any
}
export function OrderByTournamentTemplate({ selectedMarkets, orientation, caller, dataGrouped }: OrderByTournamentTemplateProps): ReactElement {
  const marketsConfig = useAppSelector(state => state.sport.marketsConfig);
  const selectedMarketsFull = (selectedMarkets || []).map(mId => (marketsConfig?.find(m => m.id == mId) as IMarketConfig))
  const [oddWidth, setOddWidth] = useState<number | undefined>(undefined)

  useEffect(() => {
    let oddNr = 0;
    selectedMarkets && selectedMarkets.map(market => {
      const foundMarket = marketsConfig?.find(m => m.id === market)
      if (foundMarket) {
        oddNr += foundMarket.odds.length
      }
    })
    if (oddNr) {
      setOddWidth(oddNr)
    }
  }, [selectedMarkets])

  return (
    <Box id="printOddTable" className={`${styles.tablePdfContainer} ${caller === "printDoc" && styles.blackText}`}>
      <Box className={`${styles.row}`} sx={{ backgroundColor: " rgb(172, 169, 169)" }}>
        <Box className={`${styles.column}`}
          sx={{ width: `${orientation === "landscape" ? "480px" : "430px"} !important`, flexDirection: "row !important" }}
        >
          <Box className={`${styles.column} ${styles.dateColumn} ${orientation === "portrait" && styles.portraitDate}`}>
            <Typography sx={{ fontSize: `11px !important`, fontWeight: 700 }}>
              <Translate value="sport.data" />
            </Typography>
          </Box>
          <Box className={styles.column} sx={{ width: "40px !important" }}>
            <Typography sx={{ fontSize: `11px !important`, fontWeight: 700 }}>
              <Translate value="sport.time" />
            </Typography>
          </Box>
          <Box className={styles.column} sx={{ width: "40px !important" }}>
            <Typography sx={{ fontSize: `11px !important`, fontWeight: 700 }}>
              <Translate value="sport.code" />
            </Typography>
          </Box>
          <Box className={`${styles.column} ${styles.eventColumn} ${orientation === "portrait" && styles.portraitEvent}`}>
            <Typography sx={{ fontSize: `11px !important`, fontWeight: 700 }}>
              <Translate value="sport.event" />
            </Typography>
          </Box>
        </Box>
        {(selectedMarketsFull.map((m: IMarketConfig) => m.odds).flat()).map((odd: IOddConfig) => {
          return <Box key={odd.id} className={styles.column}
            sx={{ width: `calc(${orientation === "landscape" ? "630px" : "503px"} / ${oddWidth}) !important` }}
          >
            <Typography sx={{ fontSize: `11px !important`, fontWeight: 700 }}>
              {odd.label}
            </Typography>
          </Box>
        })}
      </Box>
      {
        dataGrouped && Object.entries(dataGrouped).map(([index, data = []]: any) => {
          const { key, items } = data
          return <Box key={index}>
            <Box className={`${styles.row} ${styles.groupFirstRow}`} sx={{ backgroundColor: " rgb(172, 169, 169)" }} >
              <Box className={`${styles.column} `}
                sx={{ width: "100% !important", textAlign: "center !important" }}
              >
                <Typography
                  sx={{
                    fontSize: `11px !important `,
                    fontWeight: 700,
                  }}>
                  {key}
                </Typography>
              </Box>
            </Box>
            {
              items.map((event: IEventData) => {
                const fullDate = event.begin && convertBeginToDate(event.begin);
                const date = fullDate && fullDate.split(" ")[0]
                const time = fullDate && fullDate.split(" ")[1]
                return <Box key={event.id} className={styles.row}>
                  <Box className={`${styles.column} ${styles.dateColumn} ${orientation === "portrait" && styles.portraitDate}`}>
                    <Typography sx={{ fontSize: `${orientation === "landscape" ? "11px" : "11px"} !important }`, fontWeight: 700 }}>
                      {date}
                    </Typography>
                  </Box>
                  <Box className={styles.column} sx={{ width: "40px !important" }}>
                    <Typography sx={{ fontSize: `11px !important`, fontWeight: 700 }}>{time && time.slice(0, 5)}</Typography>
                  </Box>
                  <Box className={styles.column} sx={{ width: "40px !important" }}>
                    <Typography sx={{ fontSize: `11px !important`, fontWeight: 700 }}>{event.short}</Typography>
                  </Box>
                  <Box className={`${styles.column} ${styles.eventColumn} ${orientation === "portrait" && styles.portraitEvent}`}>
                    <Typography sx={{ fontSize: `${orientation === "landscape" ? "12px" : "11px"} !important }`, fontWeight: 700 }}>{event.home} - {event.away}</Typography>
                  </Box>

                  {selectedMarketsFull.map(market => {
                    const eventMarketOdds = event.markets.find(m => m.id == market.id)?.odds || []
                    return <Fragment key={market.id}>
                      {(market.odds || []).map((odd: IOddConfig) => {
                        const merge = eventMarketOdds.find(o => o.id == odd.id) || {}
                        const mergedOdd = {
                          ...odd, ...merge
                        }
                        return <Box key={mergedOdd.id} className={styles.column}
                          sx={{ width: `calc(${orientation === "landscape" ? "630px" : "503px"} / ${oddWidth}) !important` }}
                        >
                          <Typography sx={{ fontSize: `11px !important`, fontWeight: 500 }}>{mergedOdd?.extra?.value && convertOddValue(mergedOdd?.extra?.value) || '-'}</Typography>
                        </Box>
                      })}
                    </Fragment>
                  })}
                </Box>
              })
            }
          </Box >
        })
      }
    </Box >
  )
}

type OrderByDateTemplateProps = {
  selectedMarkets: number[]
  orientation: string
  caller?: string
  dataGrouped: any
}
export function OrderByDateTemplate({ selectedMarkets, orientation, caller, dataGrouped }: OrderByDateTemplateProps): ReactElement {
  const marketsConfig = useAppSelector(state => state.sport.marketsConfig);
  const sports = useAppSelector(state => state.sport.sports);
  const selectedMarketsFull = (selectedMarkets || []).map(mId => (marketsConfig?.find(m => m.id == mId) as IMarketConfig))
  const [oddWidth, setOddWidth] = useState<number | undefined>(undefined)

  useEffect(() => {
    let oddNr = 0;
    selectedMarkets && selectedMarkets.map(market => {
      const foundMarket = marketsConfig?.find(m => m.id === market)
      if (foundMarket) {
        oddNr += foundMarket.odds.length
      }
    })
    if (oddNr) {
      setOddWidth(oddNr)
    }
  }, [selectedMarkets])

  return (
    <Box id="printOddTable" className={`${styles.tablePdfContainer} ${caller === "printDoc" && styles.blackText}`}>
      <Box className={styles.row} sx={{ background: " rgb(172, 169, 169)" }}>
        <Box className={styles.column} sx={{ width: "40px !important" }}>
          <Typography sx={{ fontSize: `11px !important `, fontWeight: 700 }}>
            <Translate value="sport.time" />
          </Typography>
        </Box>
        <Box className={`${styles.column} ${styles.dateColumnData} ${orientation === "portrait" && styles.portraitDateColumnData}`}>
          <Typography sx={{ fontSize: `11px !important `, fontWeight: 700 }}>
            <Translate value="sport.championship" />
          </Typography>
        </Box>
        <Box className={styles.column} sx={{ width: "40px !important" }}>
          <Typography sx={{ fontSize: `11px !important `, fontWeight: 700 }}>
            <Translate value="sport.code" />
          </Typography>
        </Box>
        <Box className={`${styles.column} ${styles.eventColumnData} ${orientation === "portrait" && styles.portraitEventColumnData}`}>
          <Typography sx={{ fontSize: `11px !important `, fontWeight: 700 }}>
            <Translate value="sport.event" />
          </Typography>
        </Box>
        {(selectedMarketsFull.map((m: IMarketConfig) => m.odds).flat()).map(
          (odd: IOddConfig) =>
            <Box
              key={odd.id}
              className={styles.column}
              sx={{ width: `calc(${orientation === "landscape" ? "630px" : "505px"} / ${oddWidth}) !important` }}
            >
              <Typography sx={{ fontSize: `11px !important `, fontWeight: 700 }}>{odd.label}</Typography>
            </Box>
        )}
      </Box>
      {Object.entries(dataGrouped).map(([index, data = []]: any) => {
        const { key, items } = data
        return <Box key={index}>
          <Box className={`${styles.row} ${styles.groupFirstRow}`} sx={{ background: " rgb(172, 169, 169)" }} >
            <Box className={`${styles.column}`}
              sx={{ width: "100% !important", }}>
              <Typography sx={{
                fontSize: `13px !important `,
                fontWeight: 700,
                textAlign: "center"
              }}
              >
                {key}
              </Typography>
            </Box>

          </Box>
          {
            items.map((event: IEventData) => {
              const fullTime = event.begin && convertBeginToDate(event.begin)
              const time = fullTime && fullTime.split(" ")[1]
              return <Box key={event.id} className={styles.row}>
                <Box className={styles.column} sx={{ width: "40px !important" }}>
                  <Typography sx={{ fontSize: `11px !important `, fontWeight: 700 }}>{time && time.slice(0, 5)}</Typography>
                </Box>
                <Box className={`${styles.column} ${styles.dateColumnData} ${orientation === "portrait" && styles.portraitDateColumnData}`}>
                  <Typography sx={{
                    fontSize: `10px !important `,
                    fontWeight: 700,
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}
                  >
                    {event?.tournamentLabel} - {recursiveSearchByType(event.category_id, sports, 'c')?.label}
                  </Typography>
                </Box>
                <Box className={styles.column} sx={{ width: "40px !important" }}>
                  <Typography sx={{ fontSize: `11px !important `, fontWeight: 700 }}>{event.short}</Typography>
                </Box>
                <Box className={`${styles.column} ${styles.eventColumnData} ${orientation === "portrait" && styles.portraitEventColumnData}`}>
                  <Typography sx={{ fontSize: `11px !important `, fontWeight: 700 }}>{event.home} - {event.away}</Typography>
                </Box>

                {selectedMarketsFull.map(market => {
                  const eventMarketOdds = event.markets.find(m => m.id == market.id)?.odds || []
                  return <Fragment key={market.id}>
                    {(market.odds || []).map((odd: IOddConfig) => {
                      const merge = eventMarketOdds.find(o => o.id == odd.id) || {}
                      const mergedOdd = {
                        ...odd, ...merge
                      }
                      return <Box
                        key={mergedOdd.id}
                        className={styles.column}
                        sx={{ width: `calc(${orientation === "landscape" ? "630px" : "503px"} / ${oddWidth}) !important` }}
                      >
                        <Typography sx={{ fontSize: `11px !important `, fontWeight: 500 }}> {mergedOdd?.extra?.value && convertOddValue(mergedOdd?.extra?.value) || '-'}</Typography></Box>
                    })}
                  </Fragment>
                })}
              </Box>
            })
          }
        </Box >
      })}
    </Box >
  );
}

type OrderByTournamentDateTemplateProps = {
  selectedMarkets: number[]
  orientation: string
  caller?: string
  dataGrouped: any
}
export function OrderByDateTournamentTemplate({ selectedMarkets, orientation, caller, dataGrouped }: OrderByTournamentDateTemplateProps): ReactElement {
  const marketsConfig = useAppSelector(state => state.sport.marketsConfig);
  const selectedMarketsFull = (selectedMarkets || []).map(mId => (marketsConfig?.find(m => m.id == mId) as IMarketConfig))

  const [oddWidth, setOddWidth] = useState<number | undefined>(undefined)

  useEffect(() => {
    let oddNr = 0;
    selectedMarkets && selectedMarkets.map(market => {
      const foundMarket = marketsConfig?.find(m => m.id === market)
      if (foundMarket) {
        oddNr += foundMarket.odds.length
      }
    })
    if (oddNr) {
      setOddWidth(oddNr)
    }
  }, [selectedMarkets])
  return (
    <Box id="printOddTable" className={`${styles.tablePdfContainer} ${caller === "printDoc" && styles.blackText}`}>
      <Box className={styles.row} sx={{ background: " rgb(172, 169, 169)" }} >
        <Box className={styles.column}>
          <Typography sx={{ fontSize: `11px !important`, fontWeight: 700 }}><Translate value="sport.time" /></Typography>
        </Box>
        <Box className={styles.column}>
          <Typography sx={{ fontSize: `11px !important`, fontWeight: 700 }}><Translate value="sport.code" /></Typography>
        </Box>
        <Box className={`${styles.column} ${styles.eventColumn}  ${styles.eventColumnNoId}`}>
          <Typography sx={{ fontSize: `11px !important`, fontWeight: 700 }}><Translate value="sport.event" /></Typography>
        </Box>

        {(selectedMarketsFull.map((m: IMarketConfig) => m.odds).flat()).map((odd: IOddConfig) => {
          return <Box key={odd.id} className={styles.column}
            sx={{ width: `calc(${orientation === "landscape" ? "630px" : "503px"} / ${oddWidth}) !important` }}
          >
            <Typography sx={{ fontSize: `11px !important`, fontWeight: 700 }}>{odd.label}</Typography>
          </Box>
        })}
      </Box>
      {
        dataGrouped.map((item: any) => {
          const { key, items } = item
          return <Box key={key}>
            <Box className={`${styles.row} ${styles.groupFirstRow}`} sx={{ background: " rgb(172, 169, 169)" }} >
              <Box className={`${styles.column} `} sx={{ width: "100% !important", }}>
                <Typography sx={{ fontSize: `11px !important`, fontWeight: 700 }}>{key}</Typography>
              </Box>
            </Box>
            {
              items.map((item: any) => {
                const { tournament, items } = item
                return <Fragment key={tournament}>
                  <Box className={`${styles.row} ${styles.groupFirstRow}`} sx={{ background: " rgb(172, 169, 169)" }} >
                    <Box className={`${styles.column}`} sx={{ width: "100% !important", }}>
                      <Typography sx={{
                        fontSize: `11px !important`,
                        fontWeight: 700,
                      }}
                      >
                        {tournament}
                      </Typography>
                    </Box>
                  </Box>
                  {
                    items.map((event: IEventData) => {
                      const fullTime = event.begin && convertBeginToDate(event.begin)
                      const time = fullTime && fullTime.split(" ")[1]
                      return <Box key={event.id} className={styles.row}>
                        <Box className={styles.column}>
                          <Typography sx={{ fontSize: `11px !important`, fontWeight: 700 }}>{time && time.slice(0, 5)}</Typography>
                        </Box>
                        <Box className={styles.column}>
                          <Typography sx={{ fontSize: `11px !important`, fontWeight: 700 }}>{event.short}</Typography>
                        </Box>
                        <Box className={`${styles.column} ${styles.eventColumn} ${styles.eventColumnNoId}`}>
                          <Typography sx={{ fontSize: `11px !important`, fontWeight: 700 }}>{event.home} - {event.away}</Typography>
                        </Box>

                        {selectedMarketsFull.map(market => {
                          const eventMarketOdds = event.markets.find(m => m.id == market.id)?.odds || []
                          return <Fragment key={market.id}>
                            {(market.odds || []).map((odd: IOddConfig) => {
                              const merge = eventMarketOdds.find(o => o.id == odd.id) || {}
                              const mergedOdd = {
                                ...odd, ...merge
                              }
                              return <Box
                                key={mergedOdd.id}
                                className={styles.column}
                                sx={{ width: `calc(${orientation === "landscape" ? "630px" : "503px"} / ${oddWidth}) !important` }}
                              >
                                <Typography sx={{ fontSize: `11px !important`, fontWeight: 500 }}>{mergedOdd?.extra?.value && convertOddValue(mergedOdd?.extra?.value) || '-'}</Typography>
                              </Box>
                            })}
                          </Fragment>
                        })}
                      </Box>
                    })
                  }
                </Fragment >
              })
            }
          </Box >
        })}
    </Box >
  )
}

